import React, { createContext, useContext, useState, useCallback } from 'react';

interface Notification {
  id: string;
  message: string;
  isExiting?: boolean;
}

interface NotificationContextType {
  notification: Notification | null;
  showNotification: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notification, setNotification] = useState<Notification | null>(null);

  const showNotification = useCallback((message: string) => {
    const newId = Math.random().toString(36).substring(7);
    
    // If there's an existing notification, mark it for exit animation
    if (notification) {
      setNotification(prev => prev ? { ...prev, isExiting: true } : null);
      
      // Wait for exit animation to complete before showing new notification
      setTimeout(() => {
        setNotification({ id: newId, message });
      }, 200); // Match this with CSS animation duration
    } else {
      // If no existing notification, show immediately
      setNotification({ id: newId, message });
    }

    // Remove notification after delay
    setTimeout(() => {
      setNotification(prev => {
        if (prev?.id === newId) {
          return { ...prev, isExiting: true };
        }
        return prev;
      });
      
      // Actually remove after exit animation
      setTimeout(() => {
        setNotification(prev => prev?.id === newId ? null : prev);
      }, 200);
    }, 3000);
  }, [notification]);

  return (
    <NotificationContext.Provider value={{ notification, showNotification }}>
      {children}
    </NotificationContext.Provider>
  );
}; 